import { styled, css } from '@vebox/etui-system';
import '../utilities/format-ms.js';
import '../utilities/create-transition.js';
import { PureComponent, createElement, Children, cloneElement } from 'react';
import '../utilities/get-styleprop.js';
import { c as _defineProperty, a as _objectSpread2, b as _extends } from '../_rollupPluginBabelHelpers-3b7bd988.js';
import { Div } from '@vebox/etui-system-elements';
import '../H5/H5.js';
import '../Svg-0180d7b5.js';
import '../Icon/Icon.js';
import '@vebox/etui-icons';
import { icons } from '../Icon/icons.js';
import '../Icon/helper.js';
import '@vebox/etui-colors';
import '../ClickOutside/ClickOutside.js';
import '../Tooltip/Tooltip.js';
import Button from '../Button/Button.js';
import '../H4/H4.js';
import ListItemMeta from './ListItemMeta.js';

const ListItemHeaderDiv = styled(Div)`
  display: flex;
  flex-direction: row;
  align-items: center;
  max-width: 100%;
  min-width: 0;
  overflow-x: hidden;
  color: inherit;
  background: transparent;
  position: relative;

  ${({
  handleClick
}) => handleClick && css`
      cursor: pointer;

      &:active,
      &:focus,
      &:hover {
        background-color: ${({
  theme
}) => {
  return theme.palette.action.hover;
}};
      }
    `};

  ${({
  isCompact,
  theme
}) => {
  if (isCompact) {
    return css`
        min-height: 64px;
        padding: 0 ${theme.spacing.md};
      `;
  }
  return css`
      min-height: 84px;
      padding: ${theme.spacing.sm} ${theme.spacing.md};
    `;
}};

  ${({
  isSelected,
  theme
}) => isSelected && css`
      background-color: ${theme.palette.action.hover};
    `};
`;

const ListItemDiv = styled(Div)`
  ${({
  theme
}) => css`
    font-family: ${theme.typography.fontFamily};
    font-size: ${theme.typography.fontSizeBase};
    font-weight: ${theme.typography.fontWeightRegular};
    line-height: ${theme.typography.lineHeightBase};
    color: ${theme.palette.text.primary};
  `};
  margin: 0;
  transition: background-color 0.2s linear;

  &:hover {
    ${({
  onClick,
  theme
}) => onClick && css`
        background-color: ${theme.palette.action.hover};
        cursor: pointer;
      `};
  }

  ${({
  isSubItem,
  theme
}) => {
  if (isSubItem) {
    return css`
        border-bottom: 1px solid ${theme.palette.divider};

        :last-of-type {
          border: 0;
        }
      `;
  }
  return css`
      border-bottom: 1px solid ${theme.palette.divider};
    `;
}};
`;

const ListItemActionDiv = styled(Div)`
  flex: 0 0 auto;
  padding: 0;
`;

class ListItem extends PureComponent {
  constructor(props) {
    super(props);
    _defineProperty(this, "keys", {});
    _defineProperty(this, "toggleIsExpanded", event => {
      if (event) {
        event.stopPropagation();
      }
      this.setState(prevState => ({
        isExpanded: !prevState.isExpanded
      }), () => this.props.onToggleExpand && this.props.onToggleExpand(this.state.isExpanded));
    });
    _defineProperty(this, "onRenderActionItems", items => {
      return items.map(item => {
        const actionButtonProps = _objectSpread2(_objectSpread2({}, item), {}, {
          onClick: event => {
            event.stopPropagation();
            item.onClick();
          }
        });
        return /*#__PURE__*/createElement(Button, _extends({
          key: item.key,
          small: true,
          variant: "tertiary"
        }, actionButtonProps));
      });
    });
    _defineProperty(this, "renderItem", (item, index) => {
      const {
        subItems,
        renderItem,
        itemKey
      } = this.props;
      let key;
      if (typeof itemKey === 'function') {
        key = itemKey(subItems[index]);
      } else if (typeof itemKey === 'string') {
        key = subItems[itemKey];
      }
      if (!key) {
        key = `listItem-${index}`;
      }
      this.keys[index] = key;
      if (typeof renderItem === 'function') return renderItem(item, index);
      return null;
    });
    _defineProperty(this, "getHasAnySubItems", subItems => {
      if (subItems && Array.isArray(subItems)) {
        return subItems.length > 0;
      }
      return subItems !== undefined;
    });
    _defineProperty(this, "getSubItemToRender", subItems => {
      if (this.getHasAnySubItems(subItems) && Array.isArray(subItems) && subItems.length > 0) {
        const items = subItems.map((item, index) => this.renderItem(item, index));
        return Children.map(items, (child, index) => /*#__PURE__*/cloneElement(child, {
          key: this.keys[index],
          isCompact: this.props.isCompact,
          isSubItem: true
        }));
      }
      return subItems;
    });
    this.state = {
      isExpanded: false
    };
  }

  // eslint-disable-next-line react/sort-comp

  render() {
    const {
      isSelected,
      onClick,
      children,
      actions,
      isCompact,
      subItems,
      isSubItem,
      styles
    } = this.props;
    const {
      isExpanded
    } = this.state;
    const hasAnySubItems = this.getHasAnySubItems(subItems);
    const childrenContent = this.getSubItemToRender(subItems);
    const metaContent = [];
    const otherContent = [];
    Children.forEach(children, element => {
      if (element && element.type && element.type === ListItemMeta) {
        metaContent.push(element);
      } else {
        otherContent.push(element);
      }
    });
    return /*#__PURE__*/createElement(ListItemDiv, _extends({
      isSubItem: isSubItem,
      isCompact: isCompact,
      isSelected: isSelected,
      isExpanded: isExpanded,
      onClick: onClick,
      testId: "listItem"
    }, styles.root), /*#__PURE__*/createElement(ListItemHeaderDiv, {
      isSubItem: isSubItem,
      isCompact: isCompact,
      isSelected: isSelected,
      isExpanded: isExpanded,
      onClick: onClick
    }, Children.map(metaContent, child => /*#__PURE__*/cloneElement(child, {
      isCompact
    })), otherContent, (actions && actions.length !== 0 || hasAnySubItems) && /*#__PURE__*/createElement(ListItemActionDiv, {
      testId: "listItemAction"
    }, actions && actions.length !== 0 && this.onRenderActionItems(actions), hasAnySubItems && /*#__PURE__*/createElement(Button, {
      key: "expand",
      iconProps: {
        glyph: icons.CHEVRON_DOWN,
        rotate: isExpanded ? 180 : 0
      },
      onClick: this.toggleIsExpanded,
      testId: "chevron",
      variant: "tertiary",
      small: true
    }))), isExpanded && /*#__PURE__*/createElement(Div, null, childrenContent));
  }
}
_defineProperty(ListItem, "defaultProps", {
  styles: {}
});
_defineProperty(ListItem, "Meta", ListItemMeta);

export default ListItem;
export { ListItem };
